/**
 * <sticky-header>
 * Ouvre et ferme le mega-menu et le tiroir mobile. Le JS ne construit
 * aucun balisage : les panneaux sont rendus par Liquid, il ne fait que
 * basculer leur visibilite et tenir les attributs ARIA a jour.
 *
 * Sur bureau le panneau s'ouvre au survol comme au clic. Le survol a un
 * delai de fermeture : sans lui, traverser un blanc entre le bouton et le
 * panneau referme le menu sous le curseur.
 */

const CLOSE_DELAY = 180;

class StickyHeader extends HTMLElement {
  connectedCallback() {
    this.triggers = [...this.querySelectorAll('[data-mega-trigger]')];
    this.panels = [...this.querySelectorAll('[data-mega-panel]')];
    this.scrim = this.querySelector('[data-mega-scrim]');
    this.toggle = this.querySelector('[data-nav-toggle]');
    this.drawer = this.querySelector('#rc-nav-drawer');
    this.openKey = null;
    this.closeTimer = null;

    this.triggers.forEach((trigger) => {
      const key = trigger.dataset.megaTrigger;

      trigger.addEventListener('click', () => {
        if (this.openKey === key) {
          this.closeMega();
        } else {
          this.openMega(key);
        }
      });

      trigger.addEventListener('mouseenter', () => this.scheduleOpen(key));
      trigger.addEventListener('mouseleave', () => this.scheduleClose());
      trigger.addEventListener('focus', () => this.openMega(key));
    });

    this.panels.forEach((panel) => {
      panel.addEventListener('mouseenter', () => this.cancelClose());
      panel.addEventListener('mouseleave', () => this.scheduleClose());
    });

    if (this.scrim) this.scrim.addEventListener('click', () => this.closeMega());

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') this.closeAll();
    });

    // Un clic hors de l'en-tete referme tout.
    document.addEventListener('click', (event) => {
      if (!this.contains(event.target)) this.closeAll();
    });

    // Le focus qui sort de l'en-tete au clavier referme le panneau.
    this.addEventListener('focusout', (event) => {
      if (!this.contains(event.relatedTarget)) this.closeMega();
    });

    if (this.toggle && this.drawer) {
      this.toggle.addEventListener('click', () => this.toggleDrawer());
      this.setupDrawerPanels();
    }
  }

  /* --- Tiroir mobile a panneaux ------------------------------------------
     Les panneaux sont deja dans le DOM. On se contente de designer l'actif
     et de tenir une pile pour le retour. */
  setupDrawerPanels() {
    this.panelsByKey = new Map();
    this.drawer.querySelectorAll('[data-panel]').forEach((panel) => {
      this.panelsByKey.set(panel.dataset.panel, panel);
    });
    this.panelStack = ['root'];

    this.drawer.addEventListener('click', (event) => {
      const open = event.target.closest('[data-panel-open]');
      if (open) {
        this.pushPanel(open.dataset.panelOpen);
        return;
      }
      const back = event.target.closest('[data-panel-back]');
      if (back) {
        this.popPanel();
        return;
      }
      // La recherche s'ouvre par-dessus : le tiroir doit lui laisser la place.
      if (event.target.closest('[data-search-open]')) this.closeDrawer();
    });
  }

  pushPanel(key) {
    const next = this.panelsByKey.get(key);
    if (!next) return;

    const current = this.panelsByKey.get(this.panelStack[this.panelStack.length - 1]);
    if (current) {
      delete current.dataset.panelActive;
      current.dataset.panelBehind = 'true';
    }

    next.hidden = false;
    // Force un reflow pour que la transition parte de la position de repos.
    void next.offsetWidth;
    next.dataset.panelActive = 'true';
    next.scrollTop = 0;

    this.panelStack.push(key);
  }

  popPanel() {
    if (this.panelStack.length < 2) return;

    const leaving = this.panelsByKey.get(this.panelStack.pop());
    const target = this.panelsByKey.get(this.panelStack[this.panelStack.length - 1]);

    if (leaving) {
      delete leaving.dataset.panelActive;
      window.setTimeout(() => {
        if (!leaving.dataset.panelActive) leaving.hidden = true;
      }, 320);
    }
    if (target) {
      delete target.dataset.panelBehind;
      target.dataset.panelActive = 'true';
    }
  }

  resetPanels() {
    if (!this.panelsByKey) return;
    this.panelsByKey.forEach((panel, key) => {
      delete panel.dataset.panelBehind;
      if (key === 'root') {
        panel.hidden = false;
        panel.dataset.panelActive = 'true';
      } else {
        delete panel.dataset.panelActive;
        panel.hidden = true;
      }
    });
    this.panelStack = ['root'];
  }

  /* La hauteur de l'en-tete varie : la barre d'annonce est visible en haut
     de page et disparait au defilement. On mesure plutot que de supposer. */
  measureHeaderOffset() {
    const bottom = this.getBoundingClientRect().bottom;
    this.style.setProperty('--rc-header-offset', `${Math.max(0, Math.round(bottom))}px`);
  }

  scheduleOpen(key) {
    this.cancelClose();
    this.openMega(key);
  }

  scheduleClose() {
    this.cancelClose();
    this.closeTimer = window.setTimeout(() => this.closeMega(), CLOSE_DELAY);
  }

  cancelClose() {
    if (this.closeTimer) {
      window.clearTimeout(this.closeTimer);
      this.closeTimer = null;
    }
  }

  openMega(key) {
    if (this.openKey === key) return;
    this.openKey = key;

    this.panels.forEach((panel) => {
      panel.hidden = panel.dataset.megaPanel !== key;
    });
    this.triggers.forEach((trigger) => {
      trigger.setAttribute('aria-expanded', String(trigger.dataset.megaTrigger === key));
    });

    if (this.scrim) {
      this.scrim.hidden = false;
      // Force un reflow pour que la transition d'opacite parte de zero.
      void this.scrim.offsetWidth;
      this.scrim.dataset.open = 'true';
    }
  }

  closeMega() {
    this.cancelClose();
    if (this.openKey === null) return;
    this.openKey = null;

    this.panels.forEach((panel) => { panel.hidden = true; });
    this.triggers.forEach((trigger) => trigger.setAttribute('aria-expanded', 'false'));

    if (this.scrim) {
      delete this.scrim.dataset.open;
      window.setTimeout(() => {
        if (this.openKey === null) this.scrim.hidden = true;
      }, 320);
    }
  }

  toggleDrawer() {
    const open = this.toggle.getAttribute('aria-expanded') === 'true';
    if (open) {
      this.closeDrawer();
      return;
    }
    this.measureHeaderOffset();
    this.resetPanels();
    this.toggle.setAttribute('aria-expanded', 'true');
    this.drawer.hidden = false;
    document.body.style.overflow = 'hidden';
  }

  closeDrawer() {
    if (!this.toggle || !this.drawer) return;
    if (this.drawer.hidden) return;
    this.toggle.setAttribute('aria-expanded', 'false');
    this.drawer.hidden = true;
    document.body.style.overflow = '';
  }

  closeAll() {
    this.closeMega();
    this.closeDrawer();
  }
}

if (!customElements.get('sticky-header')) {
  customElements.define('sticky-header', StickyHeader);
}
